import {
  getISODay,
  isSameDay,
  isSameMinute,
  isWithinInterval,
  startOfDay,
} from "date-fns";

import {
  Calendar,
  CalendarResourceWithType,
} from "frontend/interfaces/calendar";
import { WeekViewRaw } from "frontend/interfaces/day-view";
import { parseUnit, parseUnitInterval } from "frontend/parser/parse-unit";
import { parsePerson } from "frontend/parser/parse-person";
import { mergeInterval } from "frontend/utils/base-calendar-utils";
import { header } from "frontend/uses/abstract-view/keys-and-labels";
import { PrimaryKey } from "frontend/interfaces/primary-key";
import {
  DateFormat,
  daysBeween,
  formatDate,
  weekBoundaries,
} from "shared/utils/date-utils";
import {
  Database,
  useAbstractViewParser,
} from "frontend/uses/abstract-view/use-abstract-view-parser";
import { useStore as useProfileStore } from "frontend/stores/profile";
import { parseHoliday } from "frontend/parser/settings/parse-holiday";
import { parseRoom } from "frontend/parser/parse-room";
import { parseNote } from "frontend/parser/parse-note";
import {
  getEventColumns,
  holiday,
  isNotePartOfDates,
  isNotePartOfPerson,
  note,
  onClickCreateNote,
} from "frontend/uses/abstract-view/events";

export function useWeekViewParser(
  date: Date,
  weekView: WeekViewRaw,
  personID: PrimaryKey | null,
  roomID: PrimaryKey | null
): Calendar {
  const profileStore = useProfileStore();

  const dates = daysBeween(weekBoundaries(date));
  const units = dates
    .map((date) =>
      weekView.units
        .map((unitRaw) => ({
          unitRaw,
          ...parseUnitInterval(unitRaw),
        }))
        .filter(
          ({ from, to }) =>
            isSameDay(date, from) ||
            (isWithinInterval(date, { start: from, end: to }) &&
              !isSameMinute(startOfDay(date), to))
        )
        .map(({ unitRaw }) => parseUnit(unitRaw, date))
    )
    .flat();
  const persons = weekView.persons
    .filter((person) => person.id === personID)
    .map(parsePerson);
  const rooms = weekView.rooms
    .filter((room) => room.id === roomID)
    .map(parseRoom);

  const pivotSelector = (dates: Date[]): Date[] =>
    dates.filter(
      (date: Date) =>
        profileStore.profile.calendar_shown_weekdays.indexOf(getISODay(date)) >=
        0
    );
  const resourceSelector = (database: Database): CalendarResourceWithType =>
    database.rooms[0]
      ? { ...database.rooms[0], type: "room" }
      : { ...database.persons[0], type: "person" };

  return useAbstractViewParser<Date>(
    {
      units,
      dates,
      persons,
      rooms,
    },
    {
      calculateColumnPivots: (database, _options) =>
        pivotSelector(database.dates),
      calculateColumnHeader: (date, _database, _options) =>
        header(
          `day-${formatDate(date, DateFormat.DateOnlyISO)}`,
          formatDate(date, DateFormat.DateOnlyWithoutYearWithWeekday)
        ),
      calculateColumnEvent: (pivot, database, _options) => {
        if (!personID) return {};
        return {
          onClick: onClickCreateNote(database, personID, pivot),
        };
      },
      calculateEvents: (database, _options) => {
        const dates = pivotSelector(database.dates);
        if (!personID) return [];
        const holidays = weekView.holidays
          .map(parseHoliday)
          .filter((h) => dates.some((d) => isSameDay(d, h.date)))
          .map((h) => {
            const column = dates.findIndex((d) => isSameDay(d, h.date));
            return holiday(h, personID, [column]);
          });
        const notes = weekView.notes
          .map(parseNote)
          .filter((n) => isNotePartOfPerson(n, personID))
          .filter((n) => isNotePartOfDates(n, dates))
          .map((n) => {
            const columns = getEventColumns(n.from, n.to, dates);
            return note(database, n, personID, columns, dates);
          });
        return [...holidays, ...notes];
      },
      calculateCellAt: (
        date,
        intervalReferenceDate,
        columnIntervalReferenceDate,
        database,
        _options
      ) => ({
        resource: resourceSelector(database),
        interval: mergeInterval(date, intervalReferenceDate),
        columnInterval: mergeInterval(date, columnIntervalReferenceDate),
        key: formatDate(date, DateFormat.DateOnlyISO),
      }),
    },
    {}
  );
}
